/**
 + * Login screen to enter HR section.
 + */
import React from "react"
import ReactGA from "utils/GA"
import theme from "styles/theme-main"
import { Link } from "react-router-dom"
import { ThemeProvider } from "styled-components"
import { createTheme, MuiThemeProvider } from "@material-ui/core/styles"
import {
  AuthWrapper,
  AuthFormWrapper,
  AuthHeader,
  AuthRow,
  AuthFooter,
} from "styles/Common"
import Button from "components/Button"
import TextButton from "components/TextButton"
import InputField from "components/InputField"
import ReportIssue from "components/ReportIssue"
import Text from "components/Text"
import PageTitle from "components/PageTitle"
import {
  isEmail,
  convertMUI,
  getQueryParameters,
  getHomeRoute,
  showLoginCheck,
} from "utils/utils"
import { isNonLegalClient } from "utils/clientUtils"
import {
  isProfileMode,
  isVantageMode,
  isCandidXMode,
  isHRPath,
  isInternalMode,
} from "utils/productUtils"
import {
  generateRandomString,
  pkceChallengeFromVerifier,
} from "utils/authUtils"
import { getFormConfigurationFromOrganization } from "utils/configUtils"

export class Login extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      otpState: false,
      email: null,
      password: null,
    }
  }

  handleChange = ev => {
    this.setState({ [ev.target.id]: ev.target.value })
  }

  goToForgotPassword = () => {
    this.props.history.push("/forgot-password")
  }

  componentDidMount() {
    if (!isVantageMode() && !isHRPath() && !isCandidXMode()) {
      window.location.href = "/roles"
    }
    this.props.initOtp()
    if (this.props.account.isLoggedIn && !this.props.user.loading) {
      this.loadLogged()
    }
  }

  loadLogged = (props = this.props) => {
    const { history, organization } = this.props
    const query = getQueryParameters(this.props.location)
    if (query.url) {
      history.push({
        pathname: decodeURIComponent(query.url),
        search: decodeURIComponent(query.search),
      })
    } else {
      const home = getHomeRoute(props.user, organization, true)
      if (home.pathname) {
        history.push({
          ...home,
        })
      } else {
        history.push(home)
      }
    }
  }

  componentWillUpdate(nextProps) {
    if (!this.props.account.setOtp && nextProps.account.setOtp) {
      this.setState({
        otpState: true,
        loading: false,
      })
    }
    const loggedIn =
      !this.props.account.isLoggedIn &&
      nextProps.account.isLoggedIn &&
      !this.props.user.loading
    const userLoaded =
      this.props.account.isLoggedIn &&
      this.props.user.loading &&
      !nextProps.user.loading
    if (loggedIn || userLoaded) {
      this.loadLogged(nextProps)
    }
  }

  loginAction = async ev => {
    if (ev) ev.preventDefault()

    ReactGA.event({
      category: "Candidate Metrics",
      action: "Login",
      label: "Login",
      value: 1,
    })

    const { email, password, otp } = this.state
    const { login, organization = {} } = this.props
    const showLogin = showLoginCheck(organization)
    // const showLogin = false

    this.setState({
      loading: true,
    })

    const query = getQueryParameters(this.props.location)

    if (query.url) {
      login(email, password, organization.id, otp)
      // login(email, password, organization.id, this.props.history, decodeURIComponent(query.url), decodeURIComponent(query.search), otp);
    } else {
      if (showLogin) {
        login(email, password, organization.id, otp)
        // login(email, password, organization.id, this.props.history, null, null, otp);
      } else {
        const configuration = getFormConfigurationFromOrganization(organization)
        if (configuration.oauth?.pkce) {
          var state = generateRandomString()
          localStorage.setItem("pkce_state", state)

          // Create and store a new PKCE code_verifier (the plaintext random secret)
          var code_verifier = generateRandomString()
          localStorage.setItem("pkce_code_verifier", code_verifier)

          // Hash and base64-urlencode the secret to use as the challenge
          var code_challenge = await pkceChallengeFromVerifier(code_verifier)

          window.location.href =
            configuration.oauth?.url +
            "?response_type=code" +
            "&client_id=" +
            encodeURIComponent(configuration.oauth?.id) +
            "&state=" +
            encodeURIComponent(state) +
            "&scope=openid" +
            `&redirect_uri=${window.location.origin}/oauth` +
            "&code_challenge=" +
            encodeURIComponent(code_challenge) +
            "&code_challenge_method=S256"
        } else {
          window.location.href = `${configuration.oauth?.url}&client_id=${configuration.oauth?.id}&redirect_uri=${window.location.origin}/oauth`
        }
      }
    }
  }

  render() {
    const { email, password, otp } = this.state
    const { loading, organization } = this.props
    if (organization) {
      const orgtheme = JSON.parse(organization.themeConfiguration || "{}")
      theme.logoWidth = orgtheme.logoWidth
      theme.logoMargin = orgtheme.logoMargin
      if (orgtheme.headerBGcolour) {
        theme.colors.headerBGcolour = orgtheme.headerBGcolour
      } else {
        theme.colors.headerBGcolour = "#ffffff"
      }
      theme.typography.fonts.regular = "Europa"
      theme.colors.primary = orgtheme.secondary || theme.colors.secondary
      theme.colors.secondary = orgtheme.secondary || theme.colors.secondary
      theme.menuItems.active = theme.colors.secondary
      theme.menuItems.selected = theme.colors.secondary
      theme.buttons.primary.background = theme.colors.secondary
      if (isHRPath()) {
        document.title = `${organization.name} | HR`
      }
    } else {
      document.title = isNonLegalClient(organization)
        ? "Candid"
        : isInternalMode()
        ? `Rare`
        : `Vantage | Connect with legal employers`
    }
    const muitheme = createTheme(convertMUI(theme))
    const showLogin = showLoginCheck(organization)
    // const showLogin = false

    const header = showLogin
      ? "Login"
      : `Login with ${this.props.organization.name}`
    const disableLogin = showLogin
      ? this.state.otpState
        ? !otp
        : !email || !password
      : false

    return (
      <ThemeProvider theme={theme}>
        <MuiThemeProvider theme={muitheme}>
          <AuthWrapper>
            <PageTitle label={header} variant={"account"} />
            {showLogin && (
              <Text variant={"text"} as={"div"}>
                To login please enter your account details.{" "}
                {isVantageMode() && (
                  <span>
                    If you do not have an account please{" "}
                    <Link to={"/signup"}>sign up here</Link>.
                  </span>
                )}
              </Text>
            )}

            <AuthFormWrapper>
              {showLogin && (
                <React.Fragment>
                  <AuthHeader>
                    <Text variant={"title"} weight={"bold"}>
                      Enter your details
                    </Text>
                  </AuthHeader>

                  {this.state.otpState ? (
                    <AuthRow>
                      <InputField
                        id={"otp"}
                        type={"otp"}
                        shrink
                        placeholder={"One time password"}
                        onChange={this.handleChange}
                        label={"One time password"}
                        onEnter={() => {
                          if (password && email && otp) {
                            this.loginAction()
                          }
                        }}
                      />
                    </AuthRow>
                  ) : (
                    <React.Fragment>
                      <AuthRow>
                        <InputField
                          id={"email"}
                          type={"email"}
                          shrink
                          placeholder={"Email address"}
                          onChange={this.handleChange}
                          label={"Email address"}
                          error={!isEmail(email)}
                          errorMessage={"Invalid email"}
                          onEnter={() => {
                            if (password && email) {
                              this.loginAction()
                            }
                          }}
                        />
                      </AuthRow>
                      <AuthRow>
                        <InputField
                          id={"password"}
                          shrink
                          placeholder={"Password"}
                          onChange={this.handleChange}
                          onEnter={() => {
                            if (password && email) {
                              this.loginAction()
                            }
                          }}
                          label={"Password"}
                          type={"password"}
                          error={!password}
                          errorMessage={"Required"}
                        />
                      </AuthRow>
                    </React.Fragment>
                  )}
                </React.Fragment>
              )}

              <AuthFooter>
                <Button
                  id="loginButton"
                  label={"Login"}
                  disabled={disableLogin}
                  loading={loading}
                  onClick={this.loginAction}
                  type={"button"}
                />
                {showLogin && (
                  <TextButton
                    label={"Forgot password"}
                    onClick={this.goToForgotPassword}
                  />
                )}
              </AuthFooter>
            </AuthFormWrapper>
          </AuthWrapper>
          {isProfileMode() ? <ReportIssue /> : null}
        </MuiThemeProvider>
      </ThemeProvider>
    )
  }
}

export default Login
